import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  KeyRound,
  Smartphone,
  UserPlus,
  X,
} from 'lucide-react';
import apiClientsService from '../../services/api-clients.service';

const STORAGE_KEY = 'getting_started_dismissed';

const STEPS = [
  {
    id: 'api-client',
    icon: KeyRound,
    title: 'Create an API client',
    description: 'Generate credentials to authenticate your backend against the Partner Gateway.',
    href: '/api-clients',
    cta: 'Go to API Clients',
  },
  {
    id: 'customer',
    icon: UserPlus,
    title: 'Add your first customer',
    description: 'Onboard a customer so they can trade through your integration.',
    href: '/customers',
    cta: 'Go to Customers',
  },
  {
    id: 'embed',
    icon: Smartphone,
    title: 'Explore the Embedded App',
    description: 'Read the session-token guide and understand the launch URL contract.',
    href: '/developers/embed',
    cta: 'View guide',
  },
];

/* Derive completed steps from live data passed in from Dashboard */
const deriveCompleted = (hasApiClient, hasCustomer) => {
  const completed = new Set();
  if (hasApiClient) completed.add('api-client');
  if (hasCustomer) completed.add('customer');
  return completed;
};

const StepRow = ({ step, done }) => {
  const Icon = step.icon;
  return (
    <div className={`flex items-start gap-4 py-4 border-b border-ink-100 last:border-0 transition-opacity ${done ? 'opacity-50' : ''}`}>
      <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5
        ${done
          ? 'bg-success-100 border border-success-200'
          : 'bg-accent-50 border border-accent-100'
        }`}>
        {done
          ? <Check className="h-4 w-4 text-success-600" />
          : <Icon className="h-4 w-4 text-accent-600" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${done ? 'line-through text-ink-400' : 'text-ink-800'}`}>
          {step.title}
        </p>
        {!done && (
          <p className="mt-0.5 text-xs text-ink-500 leading-relaxed">{step.description}</p>
        )}
      </div>
      {!done && (
        <Link
          to={step.href}
          className="btn btn-sm btn-secondary shrink-0 self-center"
        >
          {step.cta}
          <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
};

const GettingStartedBanner = ({ totalCustomers, customersLoading }) => {
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(STORAGE_KEY));
  const [collapsed, setCollapsed] = useState(false);
  const [hasApiClient, setHasApiClient] = useState(false);
  const [apiClientLoading, setApiClientLoading] = useState(true);

  useEffect(() => {
    apiClientsService
      .list()
      .then((clients) => setHasApiClient(Array.isArray(clients) && clients.length > 0))
      .catch(() => setHasApiClient(false))
      .finally(() => setApiClientLoading(false));
  }, []);

  const loading = customersLoading || apiClientLoading;
  const hasCustomer = totalCustomers > 0;
  const completed = deriveCompleted(hasApiClient, hasCustomer);
  const completedCount = completed.size;
  const totalCount = STEPS.length;
  const allDone = completedCount === totalCount;
  const progressPct = Math.round((completedCount / totalCount) * 100);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
  };

  if (dismissed || loading) return null;

  return (
    <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden mb-6">
      {/* Header */}
      <div
        className="flex items-center gap-4 px-6 py-4 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setCollapsed((c) => !c)}
      >
        {/* Progress ring */}
        <div className="relative h-10 w-10 shrink-0">
          <svg className="h-10 w-10 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e2e8f0" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15.5"
              fill="none"
              stroke={allDone ? '#059669' : '#4f46e5'}
              strokeWidth="3"
              strokeDasharray={`${progressPct} 100`}
              strokeLinecap="round"
              pathLength="100"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-2xs font-bold text-ink-700">
            {completedCount}/{totalCount}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          {allDone ? (
            <>
              <p className="text-sm font-semibold text-success-700">Setup complete!</p>
              <p className="text-xs text-ink-500">Your integration is ready. You can dismiss this panel.</p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-ink-900">Get started with Tabadulat Partners</p>
              <p className="text-xs text-ink-500">
                {totalCount - completedCount} step{totalCount - completedCount !== 1 ? 's' : ''} remaining to complete your integration.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {allDone && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
              className="btn btn-sm btn-secondary"
            >
              Dismiss
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            className="text-ink-400 hover:text-ink-600 transition-colors p-1"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
          {collapsed
            ? <ChevronDown className="h-4 w-4 text-ink-400" />
            : <ChevronUp className="h-4 w-4 text-ink-400" />
          }
        </div>
      </div>

      {/* Progress bar */}
      {!collapsed && (
        <div className="h-0.5 bg-ink-100 mx-6">
          <div
            className={`h-0.5 rounded-full transition-all duration-500 ${allDone ? 'bg-success-500' : 'bg-accent-500'}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}

      {/* Steps */}
      {!collapsed && (
        <div className="px-6">
          {STEPS.map((step) => (
            <StepRow key={step.id} step={step} done={completed.has(step.id)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default GettingStartedBanner;
